import { Service } from 'constants/usercentrics';
import clsx from 'clsx';
import { useContext, useEffect, useState } from 'react';
import Link from 'components/ui/Link';
import TrustBox from 'components/ui/TrustBox';
import { EmbedPlaceholder } from 'components/Shared/EmbedPlaceholder';
import { sanitizeContentfulBlockId } from 'utils/contentful';
import { isServiceAllowed } from 'components/Common/CookieNotification';
import { CookieContext } from 'context/cookie';
import type { ScriptWithKey } from 'types/Utilities';
import type { IBlockEmbedFields } from 'types/contentful/generated';

let isPinterestScriptLoaded = false;
let isInstagramScriptLoaded = false;

type BaseEmbedProps = {
  id: string;
};

// INSTAGRAM
const InstagramFrame = ({ id }: BaseEmbedProps): JSX.Element => (
  <div className="w-full md:w-2/3">
    <blockquote
      className="instagram-media bg-white border-0 p-0 m-px"
      data-instgrm-captioned
      data-instgrm-permalink={`https://www.instagram.com/p/${id}/?utm_source=ig_embed&amp;utm_campaign=loading`}
      data-instgrm-version="13"
      style={{
        borderRadius: '3px',
        boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
        maxWidth: '540px',
        minWidth: '326px',
        width: '99.37%',
      }}
    >
      <div className="p-1">
        <Link
          href={`https://www.instagram.com/p/${id}/?utm_source=ig_embed&amp;utm_campaign=loading`}
        >
          <a
            className="bg-white p-0 text-center no-underline w-full leading-0"
            target="_blank"
          >
            <div className="flex flex-row grow-0 items-center rounded-full">
              <div className="h-10 w-10 mr-0.5 bg-insBg" />
              <div className="flex flex-col grow justify-center">
                <div className="grow-0 mb-1.5 h-3.5 bg-insBg rounded w-25" />
                <div className="grow-0 bg-insBg w-15 h-3.5 rounded" />
              </div>
            </div>
            <div style={{ padding: '19% 0' }} />
            <div className="block w-12.5 h-12.5 mx-auto mb-12">
              <svg
                width="50px"
                height="50px"
                viewBox="0 0 60 60"
                version="1.1"
                xmlns="https://www.w3.org/2000/svg"
              >
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g transform="translate(-511.00, -20.00)" fill="#000000">
                    <g>
                      <path d="M556.86,30.41 C554.81,30.41 553.14,32.07 553.14,34.13 C553.14,36.18 554.81,37.85 556.86,37.85 C558.92,37.85 560.59,36.18 560.59,34.13 C560.59,32.07 558.92,30.41 556.86,30.41 M541,60.65 C535.11,60.65 530.34,55.88 530.34,50 C530.34,44.11 535.11,39.34 541,39.34 C546.88,39.34 551.65,44.11 551.65,50 C551.65,55.88 546.88,60.65 541,60.65 M541,33.88 C532.1,33.88 524.88,41.1 524.88,50 C524.88,58.89 532.1,66.11 541,66.11 C549.9,66.11 557.11,58.89 557.11,50 C557.11,41.1 549.9,33.88 541,33.88 M565.37,62.10 C565.24,65.02 564.75,66.60 564.34,67.66 C563.80,69.06 563.15,70.05 562.10,71.10 C561.05,72.15 560.06,72.80 558.66,73.34 C557.60,73.75 556.02,74.24 553.10,74.37 C549.94,74.52 548.99,74.55 541,74.55 C533.00,74.55 532.05,74.52 528.89,74.37 C525.97,74.24 524.39,73.75 523.33,73.34 C521.94,72.80 520.94,72.15 519.89,71.10 C518.84,70.05 518.19,69.06 517.65,67.66 C517.24,66.60 516.75,65.02 516.62,62.10 C516.47,58.94 516.44,57.99 516.44,50 C516.44,42.00 516.47,41.05 516.62,37.89 C516.75,34.97 517.24,33.39 517.65,32.33 C518.19,30.93 518.84,29.94 519.89,28.89 C520.94,27.84 521.94,27.19 523.33,26.65 C524.39,26.24 525.97,25.75 528.89,25.62 C532.05,25.47 533.00,25.44 541,25.44 C548.99,25.44 549.94,25.47 553.10,25.62 C556.02,25.75 557.60,26.24 558.66,26.65 C560.06,27.19 561.05,27.84 562.10,28.89 C563.15,29.94 563.80,30.93 564.34,32.33 C564.75,33.39 565.24,34.97 565.37,37.89 C565.52,41.05 565.55,42.00 565.55,50 C565.55,57.99 565.52,58.94 565.37,62.10 M570.82,37.63 C570.67,34.43 570.16,32.25 569.42,30.34 C568.65,28.37 567.63,26.70 565.96,25.03 C564.29,23.36 562.62,22.34 560.65,21.57 C558.74,20.83 556.56,20.32 553.36,20.18 C550.16,20.03 549.14,20 541,20 C532.85,20 531.83,20.03 528.63,20.18 C525.43,20.32 523.25,20.83 521.34,21.57 C519.37,22.34 517.70,23.36 516.03,25.03 C514.36,26.70 513.34,28.37 512.57,30.34 C511.83,32.25 511.32,34.43 511.18,37.63 C511.03,40.83 511,41.85 511,50 C511,58.14 511.03,59.17 511.18,62.36 C511.32,65.56 511.83,67.74 512.57,69.65 C513.34,71.62 514.36,73.29 516.03,74.96 C517.70,76.63 519.37,77.65 521.34,78.42 C523.25,79.16 525.43,79.67 528.63,79.82 C531.83,79.96 532.85,80.00 541,80.00 C549.14,80.00 550.16,79.96 553.36,79.82 C556.56,79.67 558.74,79.16 560.65,78.42 C562.62,77.65 564.29,76.63 565.96,74.96 C567.63,73.29 568.65,71.62 569.42,69.65 C570.16,67.74 570.67,65.56 570.82,62.36 C570.96,59.17 571,58.14 571,50 C571,41.85 570.96,40.83 570.82,37.63" />
                    </g>
                  </g>
                </g>
              </svg>
            </div>
            <div style={{ padding: '12.5% 0' }} />
            <div className="flex flex-row items-center mb-0.5">
              <div>
                <div
                  className="rounded-full bg-insBg h-3 w-3"
                  style={{
                    transform: 'translateX(0px) translateY(7px)',
                  }}
                />
                <div
                  className="grow-0 mr-0.5 ml-0.5 bg-insBg h-3 w-3"
                  style={{
                    transform: 'rotate(-45deg) translateX(3px) translateY(1px)',
                  }}
                />
                <div
                  className="rounded-full bg-insBg h-3 w-3"
                  style={{
                    transform: 'translateX(9px) translateY(-18px)',
                  }}
                />
              </div>
              <div className="ml-1.5">
                <div className="grow-0 rounded-full h-5 w-5 bg-insBg" />
                <div
                  className="w-0 h-0"
                  style={{
                    borderTop: '2px solid transparent',
                    borderLeft: '6px solid #f4f4f4',
                    borderBottom: '2px solid transparent',
                    transform:
                      'translateX(16px) translateY(-4px) rotate(30deg)',
                  }}
                />
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <div
                  className="w-0"
                  style={{
                    borderTop: '8px solid #F4F4F4',
                    borderRight: '8px solid transparent',
                    transform: 'translateY(16px)',
                  }}
                />
                <div
                  className="grow-0 w-4 h-3 bg-insBg"
                  style={{
                    transform: 'translateY(-4px)',
                  }}
                />
                <div
                  className="w-0 h-0"
                  style={{
                    borderTop: '8px solid #F4F4F4',
                    borderLeft: '8px solid transparent',
                    transform: 'translateY(-4px) translateX(8px)',
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col grow justify-center mb-6">
              <div className="grow-0 mb-1.5 h-3.5 bg-insBg rounded w-56" />
              <div className="grow-0 h-3.5 bg-insBg rounded w-36" />
            </div>
          </a>
        </Link>
      </div>
    </blockquote>
  </div>
);

// PINTEREST
type PinterestProps = BaseEmbedProps & {
  size?: 'small' | 'medium' | 'large';
};

const PinterestFrame = ({
  id,
  size = 'small',
}: PinterestProps): JSX.Element => (
  <div>
    <Link href={`https://www.pinterest.com/pin/${id}/`}>
      <a data-pin-do="embedPin" data-pin-width={size} />
    </Link>
  </div>
);

// IFRAME
type IframeProps = {
  height?: string;
  src: string;
  width?: string;
};

const IFrame = ({ height, src, width }: IframeProps): JSX.Element => (
  <div>
    <iframe src={src} height={height} width={width} frameBorder="0" />
  </div>
);

// SOUNDCLOUD
const SoundcloudFrame = ({ id }: BaseEmbedProps): JSX.Element => (
  <div className="w-full">
    <iframe
      width="100%"
      height="166"
      allow="autoplay"
      src={`https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/${id}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`}
    />
  </div>
);

// TRUSTPILOT
const TrustpilotFrame = (): JSX.Element => (
  <div className="w-full">
    <TrustBox />
  </div>
);

// INFOGRAM
const InfogramFrame = ({ id }: BaseEmbedProps): JSX.Element => (
  <div
    className="infogram-embed w-full"
    data-id={id}
    data-type="interactive"
    data-title=""
  />
);

const Embed = ({
  blockId,
  embedSource,
  iFrameHeight,
  iFrameWidth,
  pinterestSize,
  title,
  type,
  alignment = 'Left',
  titleAlignment = 'Left',
}: IBlockEmbedFields): JSX.Element => {
  const { cookies } = useContext(CookieContext);

  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (type === 'Pinterest') {
      if (!isPinterestScriptLoaded) {
        const script: ScriptWithKey = document.createElement('script');
        script.defer = true;
        script.type = 'text/plain';
        script.dataset.usercentrics = Service.PINTEREST;
        script.key = 'pinterest';
        script.src = 'https://assets.pinterest.com/js/pinit.js';
        document.head.appendChild(script);
        isPinterestScriptLoaded = true;
        return;
      }
    }

    if (type === 'Instagram') {
      if (!isInstagramScriptLoaded) {
        const script: ScriptWithKey = document.createElement('script');
        script.defer = true;
        script.type = 'text/plain';
        script.dataset.usercentrics = 'Instagram';
        script.key = 'instagram';
        script.src = 'https://www.instagram.com/embed.js';
        document.head.appendChild(script);
        isInstagramScriptLoaded = true;
        return;
      }
    }

    if (type === 'Infogram') {
      const script = document.createElement('script');
      script.type = 'text/plain';
      script.dataset.usercentrics = 'Infogram';
      script.async = true;

      script.text = `!(function (e, i, n, s) {
        var t = 'InfogramEmbeds',
          d = e.getElementsByTagName('script')[0];
        if (window[t] && window[t].initialized)
          window[t].process && window[t].process();
        else if (!e.getElementById(n)) {
          var o = e.createElement('script');
          (o.async = 1),
            (o.id = n),
            (o.src = 'https://e.infogram.com/js/dist/embed-loader-min.js'),
            d.parentNode.insertBefore(o, d);
        }
      })(document, 0, 'infogram-async');`;

      document.head.appendChild(script);
      return () => {
        if (script && script.parentNode?.nodeName === 'HEAD') {
          document.head.removeChild(script);
        }
      };
    }
  }, []);

  useEffect(() => {
    const serviceAllowed = isServiceAllowed(cookies, type);

    setAllowed(serviceAllowed.serviceStatus);
  }, [cookies]);

  let margin = 'start';
  if (alignment && alignment !== 'Left') {
    margin = alignment === 'Center' ? 'center' : 'end';
  }

  if (!allowed) {
    return <EmbedPlaceholder service={type} />;
  }

  return (
    <div
      className="mb-8 max-w-screen-xl mx-auto px-5 md:px-4"
      id={sanitizeContentfulBlockId(blockId)}
    >
      {title && (
        <h2
          className={clsx(
            'text-2-188r md:text-3xl mb-12 md:mb-9',
            `text-${titleAlignment?.toLowerCase()}`,
          )}
        >
          {title}
        </h2>
      )}
      <div className={clsx('flex', `justify-${margin}`)}>
        {type === 'Pinterest' && (
          <PinterestFrame id={embedSource} size={pinterestSize} />
        )}
        {type === 'Instagram' && <InstagramFrame id={embedSource} />}
        {type === 'Soundcloud' && <SoundcloudFrame id={embedSource} />}
        {type === 'Trustpilot' && <TrustpilotFrame />}
        {type === 'Infogram' && <InfogramFrame id={embedSource} />}
        {type === 'Iframe' && (
          <IFrame src={embedSource} height={iFrameHeight} width={iFrameWidth} />
        )}
      </div>
    </div>
  );
};

export default Embed;
